import { useEffect, useState, useMemo } from "react";
import { Modal, Button} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRegistered, faHeart as solidHeart, faWarning } from "@fortawesome/free-solid-svg-icons";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { ReactComponent as ZeusLogo } from "../../images/zeus_bolt_black_outline.svg";
import { analyzeAddress1 } from "../../utilities/formatting";
import CopyToClipboardButton from "../CopyToClipboardButton";

import AdvancedMarkersMap from "../GoogleMap";
import { propertiesAPI } from "./PropertiesAPI";
import {
  injectHardCodedRegistrationDates,
  registrationEndDateToDisplay,
  isHybridSale,
  isOnlineOrZeus,
} from "../../utilities/hybridSales";
import { hasPlottableCoordinates } from "../../utilities/coordinatesUtils";

/**
 * Modal displaying full property details, map, documents, and action buttons.
 */
const PropertyMapModal = ({
  showModal,
  setShowModal,
  selectedProperty,
  detailsResponse,
  handleGetDirections,
  isPropertyRegistered,
  getRegisterButtonLabel,
  handleRegisterOrUnregisterClick,
  isPropertyFavorite,
  getFavoriteButtonLabel,
  handleFavoriteClick,
  isLoadingInfo,
}) => {
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [showMap, setShowMap] = useState(true);

  const { isLoaded, loadError } = isLoadingInfo;

  // Primitive values so the effect re-runs only when the property actually changes,
  // not when the parent replaces selectedProperty with a new object for the same property.
  const propertyId = selectedProperty?.id;
  const saleType = selectedProperty?.saleType;
  const storedLatitude = selectedProperty?.latitude;
  const storedLongitude = selectedProperty?.longitude;

  // --- Coordinates ---
  // Use stored coordinates when present; otherwise ask the API, which geocodes
  // server-side and saves the result so the property is only geocoded once.
  useEffect(() => {
    if (!showModal || propertyId === null || propertyId === undefined) return;

    // Ignore late responses if the user opens a different property before this one resolves
    let isCurrent = true;

    const locateProperty = async () => {
      setIsLoadingMap(true);
      setCoordinates({ lat: null, lng: null });

      let coords = { lat: null, lng: null };

      if (hasPlottableCoordinates(storedLatitude, storedLongitude)) {
        coords = { lat: parseFloat(storedLatitude), lng: parseFloat(storedLongitude) };
      } else {
        try {
          const result = await propertiesAPI.locateProperty({ id: propertyId, saleType });
          if (hasPlottableCoordinates(result?.lat, result?.lng)) {
            coords = { lat: result.lat, lng: result.lng };
          }
        } catch (error) {
          console.error("Error locating property:", error);
        }
      }

      if (!isCurrent) return;
      setCoordinates(coords);
      setIsLoadingMap(false);
      setShowMap(true);
    };

    locateProperty();

    return () => {
      isCurrent = false;
    };
  }, [showModal, propertyId, saleType, storedLatitude, storedLongitude]);

  // --- Derived values ---
  const isForeclosure = selectedProperty?.saleTypeDescription === "Foreclosure";
  const isOnlineAuction = selectedProperty?.type === "Online";
  const isHybrid = isOnlineOrZeus(selectedProperty) === "Online Registration/In-Person Sale";

  const auctionDetail = injectHardCodedRegistrationDates(detailsResponse?.auctionDetail);
  const regEndDate = registrationEndDateToDisplay(auctionDetail);

  const hasCoordinates = hasPlottableCoordinates(coordinates.lat, coordinates.lng);

  const markerPosition = useMemo(
    () => ({ lat: coordinates.lat, lng: coordinates.lng }),
    [coordinates.lat, coordinates.lng],
  );

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      centered
      scrollable
      dialogClassName="property-map-modal"
      aria-labelledby="property-map-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title as="h2" id="property-map-modal-title">
          Property Map
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {selectedProperty && (
          <>
            {/* Hybrid sale warning */}
            {isHybridSale(selectedProperty?.location) && (
              <div className="hybrid-sale-warning" role="note">
                <FontAwesomeIcon icon={faWarning} aria-hidden="true" /> No on-site registration. Registration must be
                completed online prior to <strong>{regEndDate}</strong>.
              </div>
            )}
            <div className="d-flex align-items-center my-2 gap-2">
              <span style={{ fontWeight: "bold", display: "inline-block", minWidth: "80px" }}>Address:</span>
              <div>
                <span className="pe-2 my-0">
                  {analyzeAddress1(selectedProperty).streetOnlyValue}, {selectedProperty.city}, {selectedProperty.state}{" "}
                  {selectedProperty.zip}
                </span>
                <CopyToClipboardButton
                  textToCopy={`${analyzeAddress1(selectedProperty).streetOnlyValue}, ${selectedProperty.city}, ${
                    selectedProperty.state
                  } ${selectedProperty.zip}`}
                  className="copy-button"
                />
              </div>
            </div>

            {selectedProperty.saleStatusDescription && (
              <div className="d-flex align-items-center my-2 gap-2">
                <span style={{ fontWeight: "bold", display: "inline-block", minWidth: "80px" }}>Status:</span>
                <span className="pe-2 my-0">{selectedProperty.saleStatusDescription}</span>
              </div>
            )}

            {/* Map */}
            {showMap && (
              <div className="modal-map-container">
                {!isLoaded ? (
                  <p>Loading map...</p>
                ) : loadError ? (
                  <p>Failed to load Google Maps.</p>
                ) : isLoadingMap ? (
                  <p>Loading map...</p>
                ) : hasCoordinates ? (
                  <AdvancedMarkersMap
                    markerPosition={markerPosition}
                    mapContainerClassName="modal-map-google-map"
                    zoom={15}
                    center={{ lat: coordinates.lat, lng: coordinates.lng }}
                  />
                ) : (
                  <p>Coordinates not available</p>
                )}
                <Button
                  className="get-directions-button"
                  size="sm"
                  onClick={() =>
                    isLoaded &&
                    hasCoordinates &&
                    handleGetDirections?.({
                      ...selectedProperty,
                      latitude: coordinates.lat,
                      longitude: coordinates.lng,
                    })
                  }
                  disabled={!isLoaded || !hasCoordinates}
                >
                  Get Directions
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Close
        </Button>

        {/* Hybrid — online registration, in-person sale */}
        {isHybrid && (
          <Button variant="outline-primary" href="http://www.zeusauction.com" target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faRegistered} aria-hidden="true" /> Online Reg / In-Person Sale
          </Button>
        )}

        {/* Online — full Zeus auction */}
        {!isHybrid && isOnlineAuction && (
          <Button variant="outline-primary" href="http://www.zeusauction.com" target="_blank" rel="noopener noreferrer">
            <ZeusLogo width={20} height={20} /><span>Register</span>
          </Button>
        )}

        {/* Standard — non-foreclosure, non-online */}
        {!isHybrid && !isOnlineAuction && !isForeclosure && (
          <Button
            variant={isPropertyRegistered(selectedProperty) ? "secondary" : "outline-secondary"}
            onClick={() =>
              handleRegisterOrUnregisterClick({
                saleId: selectedProperty.saleId,
                auction: selectedProperty,
              })
            }
          >
            <FontAwesomeIcon icon={faRegistered} aria-hidden="true" /> {getRegisterButtonLabel(selectedProperty)}
          </Button>
        )}

        {/* Foreclosure without Zeus — no registration button */}

        <Button
          className={isPropertyFavorite(selectedProperty) ? "favorite-btn-active" : "favorite-btn-inactive"}
          variant={isPropertyFavorite(selectedProperty) ? "success" : "outline-success"}
          onClick={() => handleFavoriteClick(selectedProperty)}
        >
          <FontAwesomeIcon icon={isPropertyFavorite(selectedProperty) ? solidHeart : regularHeart} aria-hidden="true" />{" "}
          {getFavoriteButtonLabel(selectedProperty)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PropertyMapModal;